/**
 * Pure crop math for face-centred framing.
 *
 * Given a face cluster (a set of bounding boxes in normalized 0-1 video
 * coordinates), produces a crop rectangle that:
 *  - vertically encloses the face bbox with `marginFraction` margin above
 *    and below (margin is a fraction of face height);
 *  - has the requested aspect ratio in **real pixels** (regardless of the
 *    source video's intrinsic AR);
 *  - is centred on the face's horizontal centroid and vertical mid-point;
 *  - clamps inside the [0, 1] x [0, 1] frame.
 *
 * Independent of any zoom-call specifics — usable for any face + any
 * source AR + any target AR.
 */

type FaceBox = { x: number; y: number; width: number; height: number };

export interface ComputeCropInput {
  cluster: { boxes: ReadonlyArray<FaceBox> };
  /** Source video's intrinsic dimensions in pixels. */
  naturalDimensions: { width: number; height: number };
  /** Target crop aspect ratio in pixel space: width / height. */
  targetAspectRatio: number;
  /** Fraction of face height added as margin above AND below. Default 0.15. */
  marginFraction?: number;
}

export interface CropBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const DEFAULT_MARGIN_FRACTION = 0.15;

export function computeCropForFaceWithMarginAndAR({
  cluster,
  naturalDimensions,
  targetAspectRatio,
  marginFraction = DEFAULT_MARGIN_FRACTION,
}: ComputeCropInput): CropBox {
  const { boxes } = cluster;
  if (boxes.length === 0) {
    throw new Error(
      'computeCropForFaceWithMarginAndAR: cluster must have at least one box',
    );
  }

  // Face bbox in normalized coords across the whole cluster.
  let faceTop = Infinity;
  let faceBottom = -Infinity;
  let centerXSum = 0;
  for (const b of boxes) {
    if (b.y < faceTop) faceTop = b.y;
    if (b.y + b.height > faceBottom) faceBottom = b.y + b.height;
    centerXSum += b.x + b.width / 2;
  }
  const centerX = centerXSum / boxes.length;
  const centerY = (faceTop + faceBottom) / 2;
  const faceHeight = faceBottom - faceTop;

  // Crop height with vertical margins (still in normalized coords).
  let cropHeight = faceHeight * (1 + 2 * marginFraction);

  // Convert "target AR in real pixels" into a normalized crop ratio:
  //   cropWidthPx / cropHeightPx = targetAR
  //   cropWidthPx = cropWidthNorm * sourceWidth
  //   cropHeightPx = cropHeightNorm * sourceHeight
  //   ⇒ cropWidthNorm = cropHeightNorm * targetAR * (sourceHeight / sourceWidth)
  let cropWidth =
    cropHeight *
    targetAspectRatio *
    (naturalDimensions.height / naturalDimensions.width);

  // If the crop overflows the frame, scale both dimensions down together to
  // preserve the requested AR while fitting inside [0, 1].
  const overscaleH = cropHeight > 1 ? 1 / cropHeight : 1;
  const overscaleW = cropWidth > 1 ? 1 / cropWidth : 1;
  const overscale = Math.min(overscaleH, overscaleW);
  if (overscale < 1) {
    cropHeight *= overscale;
    cropWidth *= overscale;
  }

  // Centre, then clamp so the crop sits inside [0, 1].
  const x = Math.max(0, Math.min(1 - cropWidth, centerX - cropWidth / 2));
  const y = Math.max(0, Math.min(1 - cropHeight, centerY - cropHeight / 2));

  return { x, y, w: cropWidth, h: cropHeight };
}
